import { useQuery } from '@apollo/client';
import { Download, FileText, Save, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CUSTOMER_QUERY } from '@/modules/customers-pets/customers.gql';
import { PET_QUERY } from '@/modules/customers-pets/pets.gql';
import { Button } from '@/shared/ui/button';

import { ClinicalVisitStatusBadge } from './ClinicalVisitStatusBadge';

interface ClinicalRecordHeaderProps {
  folio_number: string;
  status: string;
  pet_id: string;
  pet_name: string | null;
  species: string | null;
  visit_count: number;
  open_visit_count: number;
  show_close: boolean;
  saving_all: boolean;
  on_view_record: () => void;
  on_download_record: () => void;
  on_save_all: () => void;
  on_request_close: () => void;
}

/** Sticky-feeling compact header for the clinical-record workspace. Tutor name isn't on
 *  the visit/summary payload (no backend change in this pass), so it's resolved live via
 *  the same pet→customer id hop already used for the cashier "opening" card this week. */
export function ClinicalRecordHeader({
  folio_number,
  status,
  pet_id,
  pet_name,
  species,
  visit_count,
  open_visit_count,
  show_close,
  saving_all,
  on_view_record,
  on_download_record,
  on_save_all,
  on_request_close,
}: ClinicalRecordHeaderProps): JSX.Element {
  const { t } = useTranslation();

  const { data: pet_data } = useQuery<{ pet: { customer_id: string } }>(PET_QUERY, {
    variables: { id: pet_id },
    skip: !pet_id,
    fetchPolicy: 'cache-first',
  });
  const customer_id = pet_data?.pet.customer_id ?? null;
  const { data: customer_data } = useQuery<{
    customer: { first_name: string; last_name: string | null };
  }>(CUSTOMER_QUERY, {
    variables: { id: customer_id ?? '' },
    skip: !customer_id,
    fetchPolicy: 'cache-first',
  });
  const tutor_name = customer_data?.customer
    ? `${customer_data.customer.first_name}${
        customer_data.customer.last_name ? ` ${customer_data.customer.last_name}` : ''
      }`
    : null;

  return (
    <div className="sticky top-0 z-10 space-y-2 rounded-t-lg border-b bg-card/95 p-4 backdrop-blur">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold tracking-tight">
            {t('clinical.panel_title')} · {folio_number}
          </h3>
          <p className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            <span className="font-medium">{pet_name ?? '—'}</span>
            {species ? <span>· {species}</span> : null}
            <span>
              · {t('clinical.tutor_label')}: {tutor_name ?? '—'}
            </span>
            <span>
              · {t('clinical.visit_count')}: {visit_count}
            </span>
            <span>
              · {t('clinical.open_count')}: {open_visit_count}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ClinicalVisitStatusBadge status={status} />
          <Button type="button" size="sm" variant="outline" onClick={on_view_record}>
            <FileText className="h-4 w-4" aria-hidden />
            <span>{t('clinical.view_record')}</span>
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={on_download_record}>
            <Download className="h-4 w-4" aria-hidden />
            <span>{t('clinical.download_record')}</span>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={on_save_all}
            disabled={saving_all}
          >
            <Save className="h-4 w-4" aria-hidden />
            <span>{t('clinical.save_all')}</span>
          </Button>
          {show_close ? (
            <Button type="button" size="sm" onClick={on_request_close}>
              <XCircle className="h-4 w-4" aria-hidden />
              <span>{t('clinical.close_visit')}</span>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
